/**
 * Collection Infinite Scroll
 * - Two-way infinite scroll (load next AND previous pages)
 * - Prefetches pages for instant loading
 * - Scroll restoration when navigating back from product
 * - Disabled when filters are active (Boost PFS handles filtering)
 */

(function() {
  'use strict';
  
  var LOAD_THRESHOLD = 800;
  var isLoading = false;
  var grid = null;
  var sectionId = '';
  var totalPages = 1;
  var pageCache = {};
  var isFromBfcache = false;
  var isDisabled = false;
  var scrollHandler = null;
  
  /**
   * Check if Boost PFS filters are active in URL
   */
  function hasActiveFilters() {
    var urlParams = new URLSearchParams(window.location.search);
    var filterKeys = Array.from(urlParams.keys());
    return filterKeys.some(function(key) {
      return key.startsWith('pf_') || key === 'sort';
    });
  }
  
  /**
   * Disable infinite scroll (when filters are applied)
   */
  function disable() {
    if (isDisabled) return;
    isDisabled = true;
    console.log('[Infinite Scroll] Disabled - filters applied');
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
    }
  }
  
  /**
   * Watch for URL changes to disable when filters are applied
   */
  function watchForFilterChanges() {
    var lastUrl = window.location.href;
    
    // Check periodically for URL changes
    setInterval(function() {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        if (hasActiveFilters()) {
          disable();
        }
      }
    }, 200);
  }
  
  /**
   * Initialize
   */
  function init() {
    // Skip if filters are active - let Boost PFS handle the products
    if (hasActiveFilters()) {
      console.log('[Infinite Scroll] Disabled - filters are active');
      isDisabled = true;
      return;
    }
    
    grid = document.querySelector('[data-infinite-scroll-grid]');
    if (!grid) return;
    
    sectionId = grid.dataset.sectionId;
    totalPages = parseInt(grid.dataset.lastPage, 10) || 1;
    
    // Setup scroll listener
    scrollHandler = onScroll;
    window.addEventListener('scroll', scrollHandler, { passive: true });
    
    // Watch for filter changes
    watchForFilterChanges();
    
    // Prefetch next and previous pages
    var currentPage = getCurrentPageFromCards();
    if (currentPage < totalPages) {
      prefetchPage(currentPage + 1);
    }
    if (currentPage > 1) {
      prefetchPage(currentPage - 1);
    }
    
    // Handle scroll restoration from URL hash
    handleScrollRestoration();
    
    // Setup product click tracking
    setupProductClickTracking();
    
    // Check immediately
    checkAndLoad();
  }
  
  /**
   * Get current page from first/last cards in grid
   */
  function getCurrentPageFromCards() {
    var cards = grid.querySelectorAll('.Grid__Cell[data-page]');
    if (cards.length === 0) return 1;
    return parseInt(cards[0].dataset.page, 10) || 1;
  }
  
  /**
   * Get the page of the first visible card
   */
  function getFirstCardPage() {
    var cards = grid.querySelectorAll('.Grid__Cell[data-page]');
    if (cards.length === 0) return 1;
    return parseInt(cards[0].dataset.page, 10) || 1;
  }
  
  /**
   * Get the page of the last visible card
   */
  function getLastCardPage() {
    var cards = grid.querySelectorAll('.Grid__Cell[data-page]');
    if (cards.length === 0) return 1;
    return parseInt(cards[cards.length - 1].dataset.page, 10) || 1;
  }
  
  /**
   * Prefetch a page
   */
  function prefetchPage(pageNum) {
    if (pageNum < 1 || pageNum > totalPages) return;
    if (pageCache[pageNum]) return;
    
    var url = window.location.pathname + '?page=' + pageNum + '&section_id=' + sectionId;
    
    fetch(url)
      .then(function(response) {
        if (!response.ok) throw new Error('Network error');
        return response.text();
      })
      .then(function(html) {
        pageCache[pageNum] = html;
      })
      .catch(function() {});
  }
  
  /**
   * Scroll handler
   */
  var scrollTimeout = null;
  function onScroll() {
    if (scrollTimeout) return;
    scrollTimeout = setTimeout(function() {
      scrollTimeout = null;
      checkAndLoad();
    }, 50);
  }
  
  /**
   * Check if we should load more
   */
  function checkAndLoad() {
    if (isDisabled || isLoading) return;
    
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var windowHeight = window.innerHeight;
    var documentHeight = document.documentElement.scrollHeight;
    
    var lastPage = getLastCardPage();
    var firstPage = getFirstCardPage();
    
    // Check if near bottom - load next page
    if (scrollTop + windowHeight >= documentHeight - LOAD_THRESHOLD) {
      if (lastPage < totalPages) {
        loadPage(lastPage + 1, 'next');
      }
    }
    
    // Check if near top - load previous page
    if (scrollTop <= LOAD_THRESHOLD) {
      if (firstPage > 1) {
        loadPage(firstPage - 1, 'previous');
      }
    }
  }
  
  /**
   * Load a specific page
   */
  function loadPage(pageNum, direction) {
    if (isDisabled || isLoading) return;
    if (pageNum < 1 || pageNum > totalPages) return;
    
    isLoading = true;
    
    // Check cache first
    if (pageCache[pageNum]) {
      appendProducts(pageCache[pageNum], pageNum, direction);
      return;
    }
    
    // Fetch if not cached
    var url = window.location.pathname + '?page=' + pageNum + '&section_id=' + sectionId;
    
    fetch(url)
      .then(function(response) {
        if (!response.ok) throw new Error('Network error');
        return response.text();
      })
      .then(function(html) {
        pageCache[pageNum] = html;
        appendProducts(html, pageNum, direction);
      })
      .catch(function() {
        isLoading = false;
      });
  }
  
  /**
   * Append products to grid
   */
  function appendProducts(html, pageNum, direction) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var newGrid = doc.querySelector('[data-infinite-scroll-grid]');
    
    if (!newGrid) {
      isLoading = false;
      return;
    }
    
    var newCards = newGrid.querySelectorAll('.Grid__Cell[data-page]');
    if (newCards.length === 0) {
      isLoading = false;
      return;
    }
    
    var fragment = document.createDocumentFragment();
    newCards.forEach(function(card) {
      fragment.appendChild(card.cloneNode(true));
    });
    
    if (direction === 'previous') {
      // Prepend - need to maintain scroll position
      var scrollTop = window.pageYOffset;
      var firstElement = grid.firstElementChild;
      var oldTop = firstElement ? firstElement.getBoundingClientRect().top + scrollTop : 0;
      
      grid.insertBefore(fragment, grid.firstChild);
      
      // Adjust scroll position
      if (firstElement) {
        var newTop = firstElement.getBoundingClientRect().top + window.pageYOffset;
        var diff = newTop - oldTop;
        window.scrollTo({ top: scrollTop + diff, behavior: 'instant' });
      }
    } else {
      // Append
      grid.appendChild(fragment);
    }
    
    // Update URL
    var currentViewPage = getVisiblePage();
    var newUrl = window.location.pathname + '?page=' + currentViewPage;
    history.replaceState({ page: currentViewPage }, '', newUrl);
    
    // Initialize sliders
    if (typeof window.initAllImageSliders === 'function') {
      requestAnimationFrame(function() {
        window.initAllImageSliders();
      });
    }
    
    document.dispatchEvent(new CustomEvent('collection-products-loaded'));
    
    // Prefetch next pages
    var lastPage = getLastCardPage();
    var firstPage = getFirstCardPage();
    
    if (lastPage < totalPages) prefetchPage(lastPage + 1);
    if (firstPage > 1) prefetchPage(firstPage - 1);
    
    isLoading = false;
  }
  
  /**
   * Get the page that's most visible in viewport
   */
  function getVisiblePage() {
    var cards = grid.querySelectorAll('.Grid__Cell[data-page]');
    var viewportMiddle = window.pageYOffset + (window.innerHeight / 2);
    var closestCard = null;
    var closestDistance = Infinity;
    
    cards.forEach(function(card) {
      var rect = card.getBoundingClientRect();
      var cardMiddle = rect.top + window.pageYOffset + (rect.height / 2);
      var distance = Math.abs(cardMiddle - viewportMiddle);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestCard = card;
      }
    });
    
    return closestCard ? parseInt(closestCard.dataset.page, 10) || 1 : 1;
  }
  
  /**
   * Handle scroll restoration from URL
   */
  function handleScrollRestoration() {
    var hash = window.location.hash;
    var urlParams = new URLSearchParams(window.location.search);
    var urlPage = parseInt(urlParams.get('page'), 10) || 1;
    
    if (hash || urlPage > 1) {
      // Need to load pages 1 to urlPage
      loadPagesSequentially(1, urlPage, function() {
        // After all pages loaded, scroll to hash element
        if (hash) {
          setTimeout(function() {
            var element = document.getElementById(hash.slice(1));
            if (element) {
              element.scrollIntoView({ behavior: 'instant', block: 'center' });
            }
          }, 100);
        }
      });
    }
  }
  
  /**
   * Load pages sequentially from start to end
   */
  function loadPagesSequentially(start, end, callback) {
    var firstPage = getFirstCardPage();
    
    // If we need to load previous pages
    if (start < firstPage) {
      loadPagesRecursive(firstPage - 1, start, 'previous', function() {
        // After loading previous, check if need to load next
        var lastPage = getLastCardPage();
        if (end > lastPage) {
          loadPagesRecursive(lastPage + 1, end, 'next', callback);
        } else {
          callback();
        }
      });
    } else {
      // Just load next pages if needed
      var lastPage = getLastCardPage();
      if (end > lastPage) {
        loadPagesRecursive(lastPage + 1, end, 'next', callback);
      } else {
        callback();
      }
    }
  }
  
  /**
   * Recursively load pages
   */
  function loadPagesRecursive(current, target, direction, callback) {
    if (direction === 'previous' && current < target) {
      callback();
      return;
    }
    if (direction === 'next' && current > target) {
      callback();
      return;
    }
    
    var url = window.location.pathname + '?page=' + current + '&section_id=' + sectionId;
    
    fetch(url)
      .then(function(response) { return response.text(); })
      .then(function(html) {
        pageCache[current] = html;
        
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('[data-infinite-scroll-grid]');
        
        if (newGrid) {
          var newCards = newGrid.querySelectorAll('.Grid__Cell[data-page]');
          var fragment = document.createDocumentFragment();
          newCards.forEach(function(card) {
            fragment.appendChild(card.cloneNode(true));
          });
          
          if (direction === 'previous') {
            var scrollTop = window.pageYOffset;
            var firstEl = grid.firstElementChild;
            var oldTop = firstEl ? firstEl.getBoundingClientRect().top + scrollTop : 0;
            
            grid.insertBefore(fragment, grid.firstChild);
            
            if (firstEl) {
              var newTop = firstEl.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: scrollTop + (newTop - oldTop), behavior: 'instant' });
            }
            
            loadPagesRecursive(current - 1, target, direction, callback);
          } else {
            grid.appendChild(fragment);
            loadPagesRecursive(current + 1, target, direction, callback);
          }
        } else {
          callback();
        }
      })
      .catch(function() { callback(); });
  }
  
  /**
   * Track product clicks to update URL
   */
  function setupProductClickTracking() {
    grid.addEventListener('click', function(e) {
      var link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      
      var card = link.closest('.Grid__Cell[data-product-handle]');
      if (!card) return;
      
      var handle = card.dataset.productHandle;
      var page = card.dataset.page;
      
      if (handle && page) {
        var url = new URL(window.location.href);
        url.hash = 'product-' + handle;
        url.searchParams.set('page', page);
        
        // Update URL before navigation
        history.replaceState({}, '', url.toString());
      }
    });
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Handle bfcache (Safari back-forward cache)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      // Safari restored from bfcache - just scroll to hash if needed
      var hash = window.location.hash;
      if (hash) {
        var element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'instant', block: 'center' });
        }
      }
    }
  });
})();
